import { Link } from "react-router-dom";
import { ArrowRight, Building2, Factory, MapPin, Cog, Package, Wrench, Truck, Building } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const coreServices = [
  { name: "Construction & General Contracting", href: "/services/commercial-construction", icon: Building2 },
  { name: "Warehouse Automation & MHE Sales", href: "/services/warehouse-automation", icon: Cog },
  { name: "Storage & Racking Solutions", href: "/services/warehouse-racking-systems", icon: Package },
  { name: "Installations", href: "/services/installations", icon: Wrench },
  { name: "Relocations", href: "/services/commercial-relocation-services", icon: Truck },
  { name: "Facility Management", href: "/services/facility-maintenance", icon: Building },
];

const topIndustries = [
  { name: "Warehousing & Distribution", href: "/industries/warehousing-distribution" },
  { name: "3PL & Logistics", href: "/industries/3pl-logistics" },
  { name: "E-Commerce Fulfillment", href: "/industries/ecommerce-fulfillment" },
  { name: "Manufacturing", href: "/industries/manufacturing" },
  { name: "Food & Beverage", href: "/industries/food-beverage" },
  { name: "Cold Storage", href: "/industries/cold-storage" },
];

const topLocations = [
  { name: "Ontario", href: "/locations/ontario" },
  { name: "Quebec", href: "/locations/quebec" },
  { name: "British Columbia", href: "/locations/british-columbia" },
  { name: "Alberta", href: "/locations/alberta" },
  { name: "Texas", href: "/locations/texas" },
  { name: "California", href: "/locations/california" },
  { name: "Florida", href: "/locations/florida" },
  { name: "New York", href: "/locations/new-york" },
];


const guideLinks = [
  { name: "Conveyor Systems", href: "/guides/what-is-a-conveyor-system" },
  { name: "Sortation", href: "/guides/what-is-a-sortation-system" },
  { name: "ASRS", href: "/guides/what-is-an-asrs" },
  { name: "Goods-to-Person", href: "/guides/what-is-goods-to-person" },
  { name: "AMRs & AGVs", href: "/guides/what-are-amrs-and-agvs" },
  { name: "Warehouse Robotics", href: "/guides/what-is-warehouse-robotics" },
  { name: "WCS / WES Software", href: "/guides/what-is-a-wcs-wes" },
  { name: "Pallet Racking Types", href: "/guides/types-of-pallet-racking" },
  { name: "Mezzanines", href: "/guides/what-is-a-mezzanine" },
  { name: "Pick Modules", href: "/guides/what-is-a-pick-module" },
  { name: "Rack-Supported Buildings", href: "/guides/what-is-a-rack-supported-building" },
  { name: "Construction Management", href: "/guides/what-is-construction-management" },
  { name: "Design-Build", href: "/guides/what-is-design-build" },
  { name: "General Contracting", href: "/guides/what-is-general-contracting" },
  { name: "Turnkey Delivery", href: "/guides/what-is-turnkey-facility-delivery" },
  { name: "Tenant Improvements", href: "/guides/what-is-a-tenant-improvement" },
  { name: "Dock Packages", href: "/guides/what-is-a-dock-package" },
  { name: "Industrial Electrical", href: "/guides/what-is-industrial-electrical-contracting" },
  { name: "Equipment Installation", href: "/guides/what-is-equipment-installation" },
  { name: "Facility Relocation", href: "/guides/what-is-a-facility-relocation" },
  { name: "Decommissioning", href: "/guides/what-is-decommissioning-liquidation" },
  { name: "Preventative Maintenance", href: "/guides/what-is-preventative-maintenance" },
  { name: "Rack Safety Inspections", href: "/guides/what-is-a-rack-safety-inspection" },
];

interface InternalLinksProps {
  /** Current page type to exclude from display */
  currentType: "service" | "industry" | "location";
  /** Current page href to exclude from links */
  currentHref?: string;
  /** Show all three sections even if currentType matches */
  showAll?: boolean;
}

const InternalLinks = ({ currentType, currentHref, showAll = false }: InternalLinksProps) => {
  const { getLocalePath } = useI18n();

  const filteredServices = coreServices.filter(s => s.href !== currentHref);
  const filteredIndustries = topIndustries.filter(i => i.href !== currentHref);
  const filteredLocations = topLocations.filter(l => l.href !== currentHref);

  const showServices = currentType !== "service" || showAll;
  const showIndustries = currentType !== "industry" || showAll;
  const showLocations = currentType !== "location" || showAll;

  return (
    <section className="py-16 bg-muted border-t border-border">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <span className="text-primary font-semibold uppercase tracking-wider text-sm">
            Explore More
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mt-2">
            Related Pages
          </h2>
        </div>

        <div className={`grid gap-10 ${
          [showServices, showIndustries, showLocations].filter(Boolean).length === 3
            ? "md:grid-cols-3"
            : "md:grid-cols-2"
        } max-w-6xl mx-auto`}>
          {/* Services Column */}
          {showServices && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Wrench className="h-5 w-5 text-primary" />
                Our Services
              </h3>
              <ul className="space-y-2">
                {filteredServices.map((service) => (
                  <li key={service.href}>
                    <Link
                      to={getLocalePath(service.href)}
                      className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                    >
                      <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      <span>{service.name}</span>
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to={getLocalePath("/services")}
                    className="text-sm font-medium text-primary hover:underline mt-2 inline-block"
                  >
                    View All Services →
                  </Link>
                </li>
              </ul>
            </div>
          )}

          {/* Industries Column */}
          {showIndustries && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Factory className="h-5 w-5 text-primary" />
                Industries We Serve
              </h3>
              <ul className="space-y-2">
                {filteredIndustries.map((industry) => (
                  <li key={industry.href}>
                    <Link
                      to={getLocalePath(industry.href)}
                      className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                    >
                      <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      <span>{industry.name}</span>
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to={getLocalePath("/industries")}
                    className="text-sm font-medium text-primary hover:underline mt-2 inline-block"
                  >
                    View All Industries →
                  </Link>
                </li>
              </ul>
            </div>
          )}

          {/* Locations Column */}
          {showLocations && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Service Locations
              </h3>
              <ul className="space-y-2">
                {filteredLocations.map((location) => (
                  <li key={location.href}>
                    <Link
                      to={getLocalePath(location.href)}
                      className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                    >
                      <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      <span>{location.name}</span>
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to={getLocalePath("/locations")}
                    className="text-sm font-medium text-primary hover:underline mt-2 inline-block"
                  >
                    View All Locations →
                  </Link>
                </li>
              </ul>
            </div>
          )}
        </div>
        <div className="mt-10 pt-8 border-t border-border">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Deep-Dive Guides</h3>
          <div className="flex flex-wrap gap-2">
            {guideLinks.map((g) => (
              <Link
                key={g.href}
                to={getLocalePath(g.href)}
                className="rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
              >
                {g.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InternalLinks;
